import { useEffect, useState } from 'react';
import type {
  BalanceSheetResponse,
  GeneralLedgerResponse,
  IncomeStatementResponse,
  StatementSectionDto,
  VatReturnResponse,
  WithholdingReportResponse,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import AgingTable, { agingHeader, agingRows, type AgingView } from './AgingTable';
import {
  amountChange,
  compareSections,
  priorDate,
  priorPeriod,
  type ComparedSection,
  type Comparison,
} from './compare';
import { downloadCsv, type CsvCell } from './csv';
import ReportActions from './ReportActions';
import GeneralLedger, { GENERAL_LEDGER_HEADER, generalLedgerRows } from './GeneralLedger';
import { formatMoney } from './money';
import VatReturn, { VAT_HEADER, vatRows } from './VatReturn';
import {
  WITHHOLDING_HEADER,
  WithholdingReceivableView,
  withholdingRows,
} from './WithholdingReport';
import Hint from './Hint';

const EMPTY_SECTION: StatementSectionDto = { rows: [], total: '0' };

/** بلا مقارنة يُقارن القسم بقسم فارغ، وأعمدة السنة السابقة تُخفى. */
function compared(
  current: StatementSectionDto,
  prior: StatementSectionDto | null,
): ComparedSection {
  return compareSections(current, prior ?? EMPTY_SECTION);
}

function sectionRows(title: string, section: ComparedSection, withPrior: boolean): CsvCell[][] {
  return [
    [title, '', '', ...(withPrior ? ['', ''] : [])],
    ...section.rows.map((row) => [
      row.code,
      row.nameAr,
      row.current,
      ...(withPrior ? [row.prior, row.change] : []),
    ]),
    [
      '',
      'إجمالى ' + title,
      section.total,
      ...(withPrior ? [section.priorTotal, section.change] : []),
    ],
  ];
}

function totalCells(label: string, current: string, prior: string | null): CsvCell[] {
  return ['', label, current, ...(prior === null ? [] : [prior, amountChange(current, prior)])];
}

interface Props {
  companyId: string;
  currency: string;
  onOpenLedger: (accountCode: string) => void;
  onOpenCustomer: (customerId: string) => void;
  onOpenSupplier: (supplierId: string) => void;
  onSessionLost: () => void;
  /** التبويب الذى تُفتح عليه — اللوحة الرئيسية تفتح الأعمار أو قائمة الدخل مباشرة. */
  initialTab?: Tab;
}

export type Tab = 'income' | 'balance' | 'general' | 'aging' | 'apAging' | 'vat' | 'withholding';

/** التبويبات التى تُقرأ لفترة (من/إلى) لا فى تاريخ. */
const PERIOD_TABS: readonly Tab[] = ['income', 'general', 'vat', 'withholding'];

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

function Section({
  title,
  section,
  withPrior,
  onOpenLedger,
}: {
  title: string;
  section: ComparedSection;
  withPrior: boolean;
  onOpenLedger: (code: string) => void;
}) {
  const columns = withPrior ? 5 : 3;
  return (
    <>
      <tr className="section-head">
        <td colSpan={columns}>{title}</td>
      </tr>
      {section.rows.map((row) => (
        <tr
          key={row.code}
          className="clickable"
          tabIndex={0}
          onClick={() => onOpenLedger(row.code)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onOpenLedger(row.code);
          }}
        >
          <td>{row.code}</td>
          <td>{row.nameAr}</td>
          <td className="num-cell">{formatMoney(row.current)}</td>
          {withPrior && <td className="num-cell">{formatMoney(row.prior)}</td>}
          {withPrior && <td className="num-cell">{formatMoney(row.change)}</td>}
        </tr>
      ))}
      {section.rows.length === 0 && (
        <tr>
          <td colSpan={columns} className="sub">
            لا حركة
          </td>
        </tr>
      )}
      <tr className="subtotal">
        <td colSpan={2}>إجمالى {title}</td>
        <td className="num-cell">{formatMoney(section.total)}</td>
        {withPrior && <td className="num-cell">{formatMoney(section.priorTotal)}</td>}
        {withPrior && <td className="num-cell">{formatMoney(section.change)}</td>}
      </tr>
    </>
  );
}

function TotalRow({
  label,
  amount,
  prior,
  className = 'closing',
}: {
  label: string;
  amount: string;
  prior: string | null;
  className?: string;
}) {
  return (
    <tr className={className}>
      <td colSpan={2}>{label}</td>
      <td className="num-cell">{formatMoney(amount)}</td>
      {prior !== null && <td className="num-cell">{formatMoney(prior)}</td>}
      {prior !== null && <td className="num-cell">{formatMoney(amountChange(amount, prior))}</td>}
    </tr>
  );
}

function HeadRow({ current, prior }: { current: string; prior: string }) {
  return (
    <thead>
      <tr>
        <th colSpan={2} />
        <th>{current}</th>
        <th>{prior}</th>
        <th>التغير</th>
      </tr>
    </thead>
  );
}

function periodLabel(statement: IncomeStatementResponse): string {
  return statement.from + ' — ' + statement.to;
}

/**
 * قائمة الدخل والمركز المالى — الأرقام من الـ API كما هى، والواجهة تعرض فقط.
 * كل حساب يُفتح كشفه بالضغط عليه، فكل رقم فى القائمة يُتتبَّع إلى قيوده.
 * المقارنة بالسنة السابقة طلب ثانٍ للـ API نفسه بالتواريخ نفسها قبل سنة —
 * لا يُحسب فى الواجهة إلا عمود التغير.
 */
export default function Reports({
  companyId,
  currency,
  onOpenLedger,
  onOpenCustomer,
  onOpenSupplier,
  onSessionLost,
  initialTab = 'income',
}: Props) {
  const year = new Date().getFullYear();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [from, setFrom] = useState(year + '-01-01');
  const [to, setTo] = useState(year + '-12-31');
  const [asOf, setAsOf] = useState(today);
  const [comparison, setComparison] = useState<Comparison>('none');
  const withPrior = comparison !== 'none';
  const [income, setIncome] = useState<IncomeStatementResponse | null>(null);
  const [priorIncome, setPriorIncome] = useState<IncomeStatementResponse | null>(null);
  const [sheet, setSheet] = useState<BalanceSheetResponse | null>(null);
  const [priorSheet, setPriorSheet] = useState<BalanceSheetResponse | null>(null);
  const [general, setGeneral] = useState<GeneralLedgerResponse | null>(null);
  const [aging, setAging] = useState<AgingView | null>(null);
  const [vat, setVat] = useState<VatReturnResponse | null>(null);
  const [withholding, setWithholding] = useState<WithholdingReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const priorRange = priorPeriod(from, to, comparison);
    const priorAsOf = priorDate(asOf, comparison);
    const load =
      tab === 'income'
        ? Promise.all([
            api.incomeStatement(companyId, from, to),
            priorRange
              ? api.incomeStatement(companyId, priorRange.from, priorRange.to)
              : Promise.resolve(null),
          ]).then(([current, prior]) => {
            if (cancelled) return;
            setIncome(current);
            setPriorIncome(prior);
          })
        : tab === 'general'
          ? api.generalLedger(companyId, from, to).then((result) => {
              if (!cancelled) setGeneral(result);
            })
          : tab === 'vat'
            ? api.vatReturn(companyId, from, to).then((result) => {
                if (!cancelled) setVat(result);
              })
            : tab === 'withholding'
              ? api.withholdingReport(companyId, from, to).then((result) => {
                  if (!cancelled) setWithholding(result);
                })
              : tab === 'aging'
                ? api.arAging(companyId, asOf).then(({ rows, ...rest }) => {
                    if (!cancelled)
                      setAging({
                        ...rest,
                        rows: rows.map(({ customerId, ...row }) => ({
                          ...row,
                          partyId: customerId,
                        })),
                      });
                  })
                : tab === 'apAging'
                  ? api.apAging(companyId, asOf).then(({ rows, ...rest }) => {
                      if (!cancelled)
                        setAging({
                          ...rest,
                          rows: rows.map(({ supplierId, ...row }) => ({
                            ...row,
                            partyId: supplierId,
                          })),
                        });
                    })
                  : Promise.all([
                      api.balanceSheet(companyId, asOf),
                      priorAsOf ? api.balanceSheet(companyId, priorAsOf) : Promise.resolve(null),
                    ]).then(([current, prior]) => {
                      if (cancelled) return;
                      setSheet(current);
                      setPriorSheet(prior);
                    });

    load
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) {
          onSessionLost();
          return;
        }
        setError(
          caught instanceof ProblemError
            ? (caught.problem.detail ?? caught.problem.title)
            : caught instanceof Error
              ? caught.message
              : String(caught),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, companyId, from, to, asOf, comparison, onSessionLost]);

  // عمود المقارنة لا يظهر إلا إن طُلب ووصل
  const pIncome = withPrior ? priorIncome : null;
  const pSheet = withPrior ? priorSheet : null;

  function exportCurrent(): void {
    if (tab === 'vat' && vat) {
      downloadCsv(
        'ملخص-القيمة-المضافة-' + vat.from + '-' + vat.to + '.csv',
        VAT_HEADER,
        vatRows(vat),
      );
    } else if (tab === 'withholding' && withholding) {
      downloadCsv(
        'الخصم-من-المنبع-' + withholding.from + '-' + withholding.to + '.csv',
        WITHHOLDING_HEADER,
        withholdingRows(withholding),
      );
    } else if ((tab === 'aging' || tab === 'apAging') && aging) {
      const customers = tab === 'aging';
      downloadCsv(
        (customers ? 'أعمار-العملاء-' : 'أعمار-الموردين-') + aging.asOf + '.csv',
        agingHeader(customers ? 'العميل' : 'المورد'),
        agingRows(aging),
      );
    } else if (tab === 'general' && general) {
      downloadCsv(
        'الأستاذ-العام-' + general.from + '-' + general.to + '.csv',
        GENERAL_LEDGER_HEADER,
        generalLedgerRows(general),
      );
    } else if (tab === 'income' && income) {
      const p = pIncome;
      const header = [
        'الكود',
        'الحساب',
        periodLabel(income),
        ...(p ? [periodLabel(p), 'التغير'] : []),
      ];
      downloadCsv('قائمة-الدخل-' + income.from + '-' + income.to + '.csv', header, [
        ...sectionRows('الإيرادات', compared(income.revenue, p?.revenue ?? null), !!p),
        ...sectionRows('تكلفة الإيراد', compared(income.costOfSales, p?.costOfSales ?? null), !!p),
        totalCells('مجمل الربح', income.grossProfit, p?.grossProfit ?? null),
        ...sectionRows('المصروفات', compared(income.expenses, p?.expenses ?? null), !!p),
        totalCells('ربح التشغيل', income.operatingProfit, p?.operatingProfit ?? null),
        ...sectionRows('إيرادات وأعباء أخرى', compared(income.other, p?.other ?? null), !!p),
        totalCells('صافى الربح (الخسارة)', income.netIncome, p?.netIncome ?? null),
      ]);
    } else if (tab === 'balance' && sheet) {
      const p = pSheet;
      const header = ['الكود', 'الحساب', sheet.asOf, ...(p ? [p.asOf, 'التغير'] : [])];
      downloadCsv('المركز-المالى-' + sheet.asOf + '.csv', header, [
        ...sectionRows('الأصول', compared(sheet.assets, p?.assets ?? null), !!p),
        ...sectionRows('الخصوم', compared(sheet.liabilities, p?.liabilities ?? null), !!p),
        ...sectionRows('حقوق الملكية', compared(sheet.equity, p?.equity ?? null), !!p),
        totalCells('نتيجة لم تُقفل بعد', sheet.unclosedResult, p?.unclosedResult ?? null),
        totalCells('إجمالى حقوق الملكية', sheet.totalEquity, p?.totalEquity ?? null),
        totalCells(
          'إجمالى الخصوم وحقوق الملكية',
          sheet.totalLiabilitiesAndEquity,
          p?.totalLiabilitiesAndEquity ?? null,
        ),
      ]);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>القوائم المالية</h2>
        {!loading &&
          ((tab === 'income' && income) ||
            (tab === 'balance' && sheet) ||
            (tab === 'general' && general) ||
            (tab === 'vat' && vat) ||
            (tab === 'withholding' && withholding) ||
            ((tab === 'aging' || tab === 'apAging') && aging)) && (
            <ReportActions onExport={exportCurrent} />
          )}
      </div>
      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'income'}
          className={tab === 'income' ? 'tab active' : 'tab'}
          onClick={() => setTab('income')}
        >
          قائمة الدخل
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'balance'}
          className={tab === 'balance' ? 'tab active' : 'tab'}
          onClick={() => setTab('balance')}
        >
          المركز المالى
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'general'}
          className={tab === 'general' ? 'tab active' : 'tab'}
          onClick={() => setTab('general')}
        >
          الأستاذ العام
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'aging'}
          className={tab === 'aging' ? 'tab active' : 'tab'}
          onClick={() => setTab('aging')}
        >
          أعمار العملاء
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'apAging'}
          className={tab === 'apAging' ? 'tab active' : 'tab'}
          onClick={() => setTab('apAging')}
        >
          أعمار الموردين
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'vat'}
          className={tab === 'vat' ? 'tab active' : 'tab'}
          onClick={() => setTab('vat')}
        >
          القيمة المضافة
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'withholding'}
          className={tab === 'withholding' ? 'tab active' : 'tab'}
          onClick={() => setTab('withholding')}
        >
          الخصم من المنبع
        </button>
      </div>

      <div className="form-row">
        {PERIOD_TABS.includes(tab) ? (
          <>
            <label>
              من
              <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </label>
            <label>
              إلى
              <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </label>
          </>
        ) : (
          <label>
            فى تاريخ
            <input type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} />
          </label>
        )}
        {(tab === 'income' || tab === 'balance') && (
          <label>
            مقارنة
            <select
              value={comparison}
              onChange={(event) => setComparison(event.target.value as Comparison)}
            >
              <option value="none">بلا مقارنة</option>
              <option value="previous">
                {tab === 'income' ? 'الفترة السابقة بنفس الطول' : 'قبل شهر'}
              </option>
              <option value="last_year">
                {tab === 'income' ? 'نفس الفترة من العام الماضى' : 'نفس التاريخ من العام الماضى'}
              </option>
            </select>
            <Hint>اعرض أرقام فترة سابقة بجانب الحالية لترى ما زاد وما نقص.</Hint>
          </label>
        )}
      </div>

      {loading && <p className="sub">جارى التحميل…</p>}
      {error && <p className="error">{error}</p>}

      {!loading && (tab === 'aging' || tab === 'apAging') && aging && (
        <AgingTable
          aging={aging}
          partyLabel={tab === 'aging' ? 'العميل' : 'المورد'}
          onOpenParty={tab === 'aging' ? onOpenCustomer : onOpenSupplier}
        />
      )}

      {!loading && tab === 'vat' && vat && <VatReturn vat={vat} currency={currency} />}

      {!loading && tab === 'withholding' && withholding && (
        <>
          <h3>ما خصمه العملاء لحسابنا</h3>
          <WithholdingReceivableView report={withholding} currency={currency} />
          {/* مكان محجوز: الخصم على الموردين يضيفه فرع موازٍ، ولا حساب له فى خريطة الحسابات بعد */}
          <h3>ما نخصمه من الموردين ونورّده</h3>
          <p className="notice">
            لم يُفعَّل بعد — الخصم من المنبع على مدفوعات الموردين قيد الإضافة، وسيظهر هنا مع جانب
            العملاء فى عرض واحد حين يُرحَّل.
          </p>
        </>
      )}

      {!loading && tab === 'general' && general && (
        <GeneralLedger gl={general} onOpenLedger={onOpenLedger} />
      )}

      {!loading && tab === 'income' && income && (
        <div className="table-scroll">
          <table className="statement">
            {pIncome && <HeadRow current={periodLabel(income)} prior={periodLabel(pIncome)} />}
            <tbody>
              <Section
                title="الإيرادات"
                section={compared(income.revenue, pIncome?.revenue ?? null)}
                withPrior={!!pIncome}
                onOpenLedger={onOpenLedger}
              />
              <Section
                title="تكلفة الإيراد"
                section={compared(income.costOfSales, pIncome?.costOfSales ?? null)}
                withPrior={!!pIncome}
                onOpenLedger={onOpenLedger}
              />
              <TotalRow
                label="مجمل الربح"
                amount={income.grossProfit}
                prior={pIncome?.grossProfit ?? null}
              />
              <Section
                title="المصروفات"
                section={compared(income.expenses, pIncome?.expenses ?? null)}
                withPrior={!!pIncome}
                onOpenLedger={onOpenLedger}
              />
              <TotalRow
                label="ربح التشغيل"
                amount={income.operatingProfit}
                prior={pIncome?.operatingProfit ?? null}
              />
              <Section
                title="إيرادات وأعباء أخرى"
                section={compared(income.other, pIncome?.other ?? null)}
                withPrior={!!pIncome}
                onOpenLedger={onOpenLedger}
              />
              <TotalRow
                label="صافى الربح (الخسارة)"
                amount={income.netIncome}
                prior={pIncome?.netIncome ?? null}
              />
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === 'balance' && sheet && (
        <>
          {(!sheet.isBalanced || (pSheet && !pSheet.isBalanced)) && (
            <p className="error">
              المركز غير متزن — الأصول لا تساوى الخصوم وحقوق الملكية. هذا يستوجب المراجعة.
            </p>
          )}
          <div className="table-scroll">
            <table className="statement">
              {pSheet && <HeadRow current={sheet.asOf} prior={pSheet.asOf} />}
              <tbody>
                <Section
                  title="الأصول"
                  section={compared(sheet.assets, pSheet?.assets ?? null)}
                  withPrior={!!pSheet}
                  onOpenLedger={onOpenLedger}
                />
                <Section
                  title="الخصوم"
                  section={compared(sheet.liabilities, pSheet?.liabilities ?? null)}
                  withPrior={!!pSheet}
                  onOpenLedger={onOpenLedger}
                />
                <Section
                  title="حقوق الملكية"
                  section={compared(sheet.equity, pSheet?.equity ?? null)}
                  withPrior={!!pSheet}
                  onOpenLedger={onOpenLedger}
                />
                <TotalRow
                  className=""
                  label="نتيجة لم تُقفل بعد إلى الأرباح المرحلة"
                  amount={sheet.unclosedResult}
                  prior={pSheet?.unclosedResult ?? null}
                />
                <TotalRow
                  label="إجمالى حقوق الملكية"
                  amount={sheet.totalEquity}
                  prior={pSheet?.totalEquity ?? null}
                />
                <TotalRow
                  label="إجمالى الخصوم وحقوق الملكية"
                  amount={sheet.totalLiabilitiesAndEquity}
                  prior={pSheet?.totalLiabilitiesAndEquity ?? null}
                />
              </tbody>
            </table>
          </div>
        </>
      )}
      {tab !== 'vat' && tab !== 'withholding' && (
        <p className="label">
          المبالغ بـ {currency}، من القيود المنشورة وحدها. اضغط أى حساب لفتح كشفه.
        </p>
      )}
    </div>
  );
}
